/**
 * CateringCartService — ephemeral cart state for building a catering order.
 *
 * RESPONSIBILITY: Manages cart items, quantities, delivery time, delivery target,
 * notes, dietary confirmation, and pre-submit validation. Cart is per-vendor
 * and resets when the vendor changes or panel closes.
 *
 * SIGNALS:
 *   items (readonly)              — CartItem[]
 *   itemCount (computed)          — total number of items
 *   totalPence (computed)         — sum of all line totals (integer, no floats)
 *   deliveryTime (readonly)       — requested delivery time
 *   deliveryTarget (readonly)     — where to deliver (room/desk/floor/ad-hoc)
 *   notes (readonly)              — special instructions
 *   dietaryConfirmed (readonly)   — checkbox state
 *   isValid (computed)            — cart meets all requirements
 *   validationErrors (computed)   — list of current issues
 *   isSubmitting (readonly)       — create+submit mutation in flight
 *   submitError (readonly)        — last error
 *   vendorId (readonly)           — current vendor
 *   vendorName (readonly)         — display name
 *   currency (readonly)           — ISO 4217 currency code (default 'GBP')
 *   idempotencyKey (readonly)     — UUID for dedup
 *   cutOffPassed (readonly)       — true if delivery time is past vendor cut-off
 *   cutOffMessage (readonly)      — human-readable cut-off explanation
 *   cartIssues (readonly)         — validation issues from CateringEdgeCaseService
 *
 * USED BY: MenuBrowseComponent, CartSummaryComponent, BookMeetingService
 * BACKEND CONTRACT: createCateringOrder + submitCateringOrder mutations
 *
 * AI CONTEXT: This is ephemeral state only — nothing is persisted to localStorage.
 * The cart resets on every initForVendor() call. The idempotencyKey is regenerated
 * each time to prevent duplicate order submission.
 */
import { Injectable, inject, signal, computed } from '@angular/core';
import {
  CateringMenuItem,
  CartItem,
  DeliveryTarget,
  CateringOrderDraft,
  CutOffCheck,
  CartValidation,
  CartItemIssue,
  VendorAvailability,
  cartLineId,
} from '../models/catering.model';
import { cartTotal, cartTax, generateIdempotencyKey } from '../utils/catering';
import { isVendorType, VendorType } from '../constants/enums';
import { AnalyticsService } from './analytics.service';
import { ANALYTICS_EVENTS } from '../analytics/events';

@Injectable({ providedIn: 'root' })
export class CateringCartService {
  private readonly analytics = inject(AnalyticsService);

  // ── Private signals ─────────────────────────────────

  private readonly _items = signal<CartItem[]>([]);
  private readonly _deliveryTime = signal<Date | null>(null);
  private readonly _deliveryTarget = signal<DeliveryTarget | null>(null);
  private readonly _notes = signal('');
  private readonly _dietaryConfirmed = signal(false);
  private readonly _isSubmitting = signal(false);
  private readonly _submitError = signal<string | null>(null);
  private readonly _vendorId = signal<string | null>(null);
  private readonly _vendorName = signal<string | null>(null);
  private readonly _vendorLogo = signal<string | null>(null);
  private readonly _vendorType = signal<string | null>(null);
  private readonly _idempotencyKey = signal('');
  private readonly _freeTextRequest = signal('');
  private readonly _headcount = signal<number | null>(null);
  private readonly _cutOffPassed = signal(false);
  private readonly _cutOffMessage = signal<string | null>(null);
  private readonly _cartIssues = signal<CartItemIssue[]>([]);
  private readonly _currency = signal<string>('GBP');
  private readonly _vendorAvailable = signal(true);
  private readonly _vendorUnavailableReason = signal<string | null>(null);
  private readonly _vendorNextAvailableSlot = signal<string | null>(null);

  // ── Public readonly signals ─────────────────────────

  readonly items = this._items.asReadonly();
  readonly deliveryTime = this._deliveryTime.asReadonly();
  readonly deliveryTarget = this._deliveryTarget.asReadonly();
  readonly notes = this._notes.asReadonly();
  readonly dietaryConfirmed = this._dietaryConfirmed.asReadonly();
  readonly isSubmitting = this._isSubmitting.asReadonly();
  readonly submitError = this._submitError.asReadonly();
  readonly vendorId = this._vendorId.asReadonly();
  readonly vendorName = this._vendorName.asReadonly();
  readonly vendorType = this._vendorType.asReadonly();
  readonly idempotencyKey = this._idempotencyKey.asReadonly();
  readonly freeTextRequest = this._freeTextRequest.asReadonly();
  readonly headcount = this._headcount.asReadonly();
  readonly cutOffPassed = this._cutOffPassed.asReadonly();
  readonly cutOffMessage = this._cutOffMessage.asReadonly();
  readonly cartIssues = this._cartIssues.asReadonly();
  readonly currency = this._currency.asReadonly();
  readonly vendorAvailable = this._vendorAvailable.asReadonly();
  readonly vendorUnavailableReason = this._vendorUnavailableReason.asReadonly();
  readonly vendorNextAvailableSlot = this._vendorNextAvailableSlot.asReadonly();

  // ── Computed signals ────────────────────────────────

  readonly itemCount = computed(() =>
    this._items().reduce((sum, item) => sum + item.quantity, 0),
  );

  /** The basket EX tax — what menu prices add up to. */
  readonly totalPence = computed(() =>
    cartTotal(this._items()),
  );

  /** The tax the order will add, line by line, as the server computes it. */
  readonly taxPence = computed(() =>
    cartTax(this._items()),
  );

  /**
   * What the order will actually cost: the basket plus its tax. This is the
   * figure the approval gate is judged on server-side, so it is the one to
   * show as the total and the one to ask the approval check about.
   */
  readonly grossPence = computed(() => this.totalPence() + this.taxPence());

  readonly isValid = computed(() => {
    const items = this._items();
    const deliveryTime = this._deliveryTime();
    const vendorType = this._vendorType();

    if (isVendorType(vendorType, VendorType.EMAIL_ONLY)) {
      return !!this._freeTextRequest() && deliveryTime !== null;
    }

    return items.length > 0 && deliveryTime !== null;
  });

  /**
   * Gate the submit button. True when isValid AND no blocking validation
   * issues have been raised by the server (PRICE_CHANGED / MAX_QTY_EXCEEDED).
   */
  readonly canSubmit = computed(() =>
    this.isValid() && this._cartIssues().length === 0,
  );

  readonly validationErrors = computed(() => {
    const errors: string[] = [];
    const items = this._items();
    const deliveryTime = this._deliveryTime();
    const vendorType = this._vendorType();

    if (isVendorType(vendorType, VendorType.EMAIL_ONLY)) {
      if (!this._freeTextRequest()) {
        errors.push('Please describe your catering request');
      }
    } else if (items.length === 0) {
      errors.push('Add at least one item to your order');
    }

    if (!deliveryTime) {
      errors.push('Select a delivery time');
    }

    const hasFoodItems = items.some(item => (item.dietaryTags?.length ?? 0) > 0);
    if (hasFoodItems && !this._dietaryConfirmed()) {
      errors.push('Confirm dietary requirements have been reviewed');
    }

    return errors;
  });

  // ── Methods ─────────────────────────────────────────

  /**
   * Set the currency the cart quotes its prices in.
   *
   * THE MENU IS THE AUTHORITY, not the vendor. `initForVendor` takes the
   * vendor's `defaultCurrency`, but that is only a prefill for the menus it
   * creates — an individual menu may be priced in something else, and the
   * order freezes the published MENU's currency at submit. So whoever loads a
   * menu says so here, and the cart bar and summary stop disagreeing with the
   * prices the employee is reading.
   *
   * Safe to call repeatedly; setting the same value is a no-op for change
   * detection because signals compare by identity.
   */
  setCurrency(currency: string): void {
    if (currency) this._currency.set(currency);
  }

  /**
   * Reset cart for a new vendor. Generates a fresh idempotency key.
   *
   * `currency` is `string | null`, not a defaulted parameter: GraphQL answers
   * `null` for a caterer saved before `defaultCurrency` existed, a default
   * parameter only covers `undefined`, and a `null` currency makes every
   * `formatCurrencyMinor` call on the cart throw.
   */
  initForVendor(vendorId: string, vendorName: string, vendorType: string, defaultDeliveryTime?: Date, vendorLogo?: string, currency?: string | null): void {
    this.reset();
    this._vendorId.set(vendorId);
    this._vendorName.set(vendorName);
    this._vendorLogo.set(vendorLogo || null);
    this._vendorType.set(vendorType);
    this._currency.set(currency || 'GBP');
    this._idempotencyKey.set(generateIdempotencyKey());
    if (defaultDeliveryTime) {
      this._deliveryTime.set(defaultDeliveryTime);
    }
  }

  /**
   * Add a menu item with specific options. Each unique option combination
   * creates a separate cart line item (like McDonald's/UberEats).
   *
   * - "Latte (Oat, Primo)" + "Latte (Almond, Massimo)" = 2 line items
   * - Adding "Latte (Oat, Primo)" again increments quantity of existing line
   * - Items with no questions use menuItemId as the line key
   */
  addItem(
    menuItem: CateringMenuItem,
    menuId: string,
    answers?: { questionName: string; answer: string }[],
  ): void {
    const lineId = cartLineId(menuItem._id, answers);

    this._items.update(items => {
      const existing = items.find(i => i.cartLineId === lineId);
      if (existing) {
        return items.map(i =>
          i.cartLineId === lineId
            ? {
                ...i,
                quantity: i.quantity + 1,
                totalCostPence: (i.quantity + 1) * i.unitCostPence,
              }
            : i,
        );
      }

      // Build display name with options (e.g., "Latte (Oat, Medio)")
      let displayName = menuItem.translatedName || menuItem.name;
      if (answers?.length) {
        const optionStr = answers.map(a => a.answer).join(', ');
        displayName = `${displayName} (${optionStr})`;
      }

      const unitCostPence = unitCostWithSurcharges(menuItem, answers);
      const newItem: CartItem = {
        cartLineId: lineId,
        menuItemId: menuItem._id,
        menuId,
        name: displayName,
        quantity: 1,
        unitCostPence,
        totalCostPence: unitCostPence,
        dietaryTags: menuItem.dietaryTags ?? [],
        allergens: menuItem.allergens ?? [],
        category: menuItem.category,
        questionAnswers: answers,
        taxRatePercent: menuItem.taxRatePercent,
      };
      return [...items, newItem];
    });
    const vendorId = this._vendorId() ?? '';
    const items = this._items();
    const matching = items.find(i => i.cartLineId === lineId);
    this.analytics.capture(ANALYTICS_EVENTS.CATERING_ITEM_ADDED, {
      vendor_id: vendorId,
      item_id: menuItem._id,
      item_name: menuItem.translatedName || menuItem.name,
      quantity: matching?.quantity ?? 1,
      unit_price_pence: matching?.unitCostPence,
      cart_total_pence: cartTotal(items),
      cart_line_count: items.length,
    });
  }

  /** Add a pre-built CartItem directly (for restoring from a draft during edit). */
  addItemDirect(item: CartItem): void {
    // Ensure cartLineId exists (backward compat)
    const withLineId = item.cartLineId
      ? item
      : { ...item, cartLineId: cartLineId(item.menuItemId, item.questionAnswers) };
    this._items.update(items => [...items, withLineId]);
  }

  /** Decrement a cart line's quantity or remove it if quantity reaches 0. */
  removeItem(lineId: string): void {
    const before = this._items();
    const target = before.find(i => i.cartLineId === lineId || i.menuItemId === lineId);
    this._items.update(items => {
      const existing = items.find(i => i.cartLineId === lineId || i.menuItemId === lineId);
      if (!existing) return items;

      const key = existing.cartLineId;
      if (existing.quantity <= 1) {
        return items.filter(i => i.cartLineId !== key);
      }

      return items.map(i =>
        i.cartLineId === key
          ? {
              ...i,
              quantity: i.quantity - 1,
              totalCostPence: (i.quantity - 1) * i.unitCostPence,
            }
          : i,
      );
    });
    const after = this._items();
    if (target) {
      this.analytics.capture(ANALYTICS_EVENTS.CATERING_ITEM_REMOVED, {
        vendor_id: this._vendorId() ?? undefined,
        item_id: target.menuItemId,
        cart_line_count: after.length,
        cart_total_pence: cartTotal(after),
      });
    }
  }

  /** Set an exact quantity for a cart line. Removes if qty <= 0. */
  setQuantity(lineId: string, qty: number): void {
    if (qty <= 0) {
      this._items.update(items => items.filter(i => i.cartLineId !== lineId && i.menuItemId !== lineId));
      return;
    }

    this._items.update(items =>
      items.map(i =>
        (i.cartLineId === lineId || i.menuItemId === lineId)
          ? { ...i, quantity: qty, totalCostPence: qty * i.unitCostPence }
          : i,
      ),
    );
  }

  /**
   * Set a question answer for a cart line's custom question.
   *
   * `menuItem` is required to recompute unit cost when an option carries a
   * surcharge (e.g. oat milk +50p). If omitted the existing unitCostPence is
   * preserved — only safe when the question has no priced options.
   */
  setQuestionAnswer(
    lineId: string,
    questionName: string,
    answer: string,
    menuItem?: CateringMenuItem,
  ): void {
    this._items.update(items =>
      items.map(i => {
        if (i.cartLineId !== lineId && i.menuItemId !== lineId) return i;

        const answers = i.questionAnswers ? [...i.questionAnswers] : [];
        const existingIdx = answers.findIndex(a => a.questionName === questionName);
        if (existingIdx >= 0) {
          answers[existingIdx] = { questionName, answer };
        } else {
          answers.push({ questionName, answer });
        }

        const newLineId = cartLineId(i.menuItemId, answers);
        const unitCostPence = menuItem
          ? unitCostWithSurcharges(menuItem, answers)
          : i.unitCostPence;
        return {
          ...i,
          cartLineId: newLineId,
          questionAnswers: answers,
          unitCostPence,
          totalCostPence: i.quantity * unitCostPence,
        };
      }),
    );
  }

  /** Get total quantity across all line items for a given menu item */
  getTotalQuantityForItem(menuItemId: string): number {
    return this._items()
      .filter(i => i.menuItemId === menuItemId)
      .reduce((sum, i) => sum + i.quantity, 0);
  }

  /** Get all cart lines for a given menu item (may be multiple with different options) */
  getLinesForItem(menuItemId: string): CartItem[] {
    return this._items().filter(i => i.menuItemId === menuItemId);
  }

  /** Set the requested delivery time. */
  setDeliveryTime(time: Date): void {
    this._deliveryTime.set(time);
  }

  /** Set the delivery location (room, desk, floor, or ad-hoc). */
  setDeliveryTarget(target: DeliveryTarget): void {
    this._deliveryTarget.set(target);
  }

  /** Set special instructions / notes. */
  setNotes(notes: string): void {
    this._notes.set(notes);
  }

  /** Set free-text request (for email-only vendors). */
  setFreeTextRequest(text: string): void {
    this._freeTextRequest.set(text);
  }

  /** Set headcount (number of people, for email-only vendors). */
  setHeadcount(count: number | null): void {
    this._headcount.set(count);
  }

  /** Set dietary confirmation checkbox state. */
  setDietaryConfirmed(confirmed: boolean): void {
    this._dietaryConfirmed.set(confirmed);
  }

  /** Mark as submitting (used by orchestrating services). */
  setSubmitting(submitting: boolean): void {
    this._isSubmitting.set(submitting);
  }

  /** Set submit error message. */
  setSubmitError(error: string | null): void {
    this._submitError.set(error);
  }

  /** Update cut-off status from CateringEdgeCaseService polling */
  setCutOffStatus(result: CutOffCheck): void {
    this._cutOffPassed.set(!result.canOrder);
    this._cutOffMessage.set(result.reason || null);
  }

  /** Update vendor availability status from CateringEdgeCaseService polling */
  setAvailabilityStatus(result: VendorAvailability): void {
    this._vendorAvailable.set(result.available);
    this._vendorUnavailableReason.set(result.reason || result.exceptionNote || null);
    this._vendorNextAvailableSlot.set(result.nextAvailableSlot || null);
  }

  /** Update cart validation issues from CateringEdgeCaseService */
  setValidationResult(result: CartValidation): void {
    this._cartIssues.set(result.issues);
  }

  /**
   * Export the current cart state as a draft for attachment to a booking.
   * Throws if the cart isn't ready (no vendor / no delivery slot) — callers
   * MUST gate on `isReadyToOrder()` before invoking. This replaces a
   * silent string of '!'-assertions with a single typed precondition
   * check that surfaces the misuse explicitly.
   */
  toDraft(roomSpaceId: string, roomName: string, dayIndex?: number): CateringOrderDraft {
    const vendorId = this._vendorId();
    const vendorName = this._vendorName();
    const vendorType = this._vendorType();
    const deliveryTime = this._deliveryTime();
    const deliveryTarget = this._deliveryTarget();
    if (!vendorId || !vendorName || !vendorType || !deliveryTime || !deliveryTarget) {
      throw new Error(
        '[CateringCart] toDraft() called before cart is ready — set vendor + delivery slot first.',
      );
    }
    return {
      vendorId,
      vendorName,
      vendorLogo: this._vendorLogo() || undefined,
      vendorType,
      items: [...this._items()],
      freeTextRequest: this._freeTextRequest() || undefined,
      headcount: this._headcount() ?? undefined,
      totalCostPence: this.totalPence(),
      currency: this._currency(),
      deliveryTime,
      deliveryTarget,
      specialInstructions: this._notes() || undefined,
      dietaryConfirmed: this._dietaryConfirmed(),
      idempotencyKey: this._idempotencyKey(),
      roomSpaceId,
      roomName,
      dayIndex,
    };
  }

  /**
   * Get allergens present in cart that conflict with user exclusions.
   * Returns array of allergen IDs that appear in cart AND are in user's exclusion list.
   *
   * @param userExclusions - Array of allergen IDs the user wants to avoid (e.g., ['gluten', 'milk'])
   * @returns Array of allergen IDs that conflict (e.g., ['milk'] if cart has latte)
   */
  getAllergenConflicts(userExclusions: string[]): string[] {
    if (!userExclusions.length) return [];

    // Flatten all allergens from all cart items
    const cartAllergenIds = this._items()
      .flatMap(item => item.allergens?.map(a => a.id) || []);

    // Find exclusions that appear in cart
    const conflicts = userExclusions.filter(excluded =>
      cartAllergenIds.includes(excluded),
    );

    // Return unique conflicts only
    return Array.from(new Set(conflicts));
  }

  /** Clear all cart state. */
  reset(): void {
    this._items.set([]);
    this._deliveryTime.set(null);
    this._deliveryTarget.set(null);
    this._notes.set('');
    this._freeTextRequest.set('');
    this._headcount.set(null);
    this._dietaryConfirmed.set(false);
    this._isSubmitting.set(false);
    this._submitError.set(null);
    this._vendorId.set(null);
    this._vendorName.set(null);
    this._vendorLogo.set(null);
    this._vendorType.set(null);
    this._currency.set('GBP');
    this._idempotencyKey.set('');
    this._cutOffPassed.set(false);
    this._cutOffMessage.set(null);
    this._cartIssues.set([]);
    this._vendorAvailable.set(true);
    this._vendorUnavailableReason.set(null);
    this._vendorNextAvailableSlot.set(null);
  }
}

/**
 * Sum the menu item base price and any priced option surcharges that match
 * the user's answers. Returns integer pence — never floats.
 */
function unitCostWithSurcharges(
  menuItem: CateringMenuItem,
  answers?: { questionName: string; answer: string }[],
): number {
  const base = menuItem.costPence ?? 0;
  if (!answers?.length || !menuItem.questions?.length) return base;

  let surcharge = 0;
  for (const answer of answers) {
    const question = menuItem.questions.find(q => q.name === answer.questionName);
    const option = question?.options?.find(o => o.label === answer.answer);
    if (option?.pricePence) surcharge += option.pricePence;
  }
  return base + surcharge;
}
